import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Sidebar } from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { 
  Menu, 
  TrendingUp, 
  TrendingDown, 
  FileText, 
  Ticket, 
  Calendar,
  Building2,
  RefreshCw
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Chart configuration constants - extracted to prevent re-renders
const CHART_COLORS = {
  newEnquiries: '#3b82f6',
  wonEnquiries: '#22c55e',
  conversionRate: '#22c55e',
  newTickets: '#f97316',
  closedTickets: '#a855f7',
  resolutionRate: '#a855f7'
};

const AXIS_STYLE = {
  fontSize: 12,
  stroke: '#64748b'
};

const CARTESIAN_GRID_STYLE = {
  strokeDasharray: '3 3',
  stroke: '#e2e8f0'
};

const DOT_CONFIG = {
  r: 3
};

const ACTIVE_DOT_CONFIG = {
  r: 6
};

export const BranchPerformance = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filters
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedDays, setSelectedDays] = useState('30');
  
  // Data
  const [branches, setBranches] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [branchName, setBranchName] = useState('All Branches');
  
  // Admin only access
  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Only admins can access this page');
      navigate('/dashboard');
    }
  }, [user, navigate]);
  
  const fetchPerformanceData = useCallback(async () => {
    try {
      setRefreshing(true);
      const params = new URLSearchParams();
      if (selectedBranch !== 'all') {
        params.append('branch_id', selectedBranch);
      }
      params.append('days', selectedDays);
      
      const response = await axios.get(
        `${API_URL}/api/branch-performance/trends?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data;
      setBranches(data.branches || []);
      setTrendData(data.trend_data || []);
      setSummary(data.summary || null);
      setBranchName(data.branch_name || 'All Branches');
      
    } catch (error) {
      // Removed console.error for production
      toast.error('Failed to fetch performance data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedBranch, selectedDays, token]);
  
  useEffect(() => {
    if (token && user?.role === 'admin') {
      fetchPerformanceData();
    }
  }, [token, user, fetchPerformanceData]);
  
  // Format date for chart tooltip - memoized
  const formatDate = useCallback((dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
  }, []);
  
  // Custom tooltip for charts - memoized component
  const CustomTooltip = useMemo(() => {
    return ({ active, payload, label }) => {
      if (active && payload && payload.length) {
        const formattedDate = new Date(label).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
        return (
          <div className="bg-white p-3 border rounded-lg shadow-lg">
            <p className="font-medium text-slate-900">{formattedDate}</p>
            {payload.map((entry) => (
              <p key={entry.dataKey} style={{ color: entry.color }} className="text-sm">
                {entry.name}: {entry.value}
                {entry.name.includes('Rate') ? '%' : ''}
              </p>
            ))}
          </div>
        );
      }
      return null;
    };
  }, []);
  
  if (!user || user.role !== 'admin') {
    return null;
  }
  
  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      
      <div className="md:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between h-16 px-4">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setSidebarOpen(true)} 
                className="md:hidden"
                data-testid="open-sidebar-button"
              >
                <Menu className="h-6 w-6" />
              </button>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-6 w-6 text-primary" />
                <h1 className="text-xl font-semibold" data-testid="page-title">Branch Performance</h1>
              </div>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPerformanceData}
              disabled={refreshing}
              data-testid="refresh-button"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </header>
        
        <main className="p-4 md:p-6">
          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Building2 className="h-4 w-4 inline mr-1" />
                    Branch
                  </label>
                  <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                    <SelectTrigger data-testid="branch-select">
                      <SelectValue placeholder="Select Branch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Branches</SelectItem>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Calendar className="h-4 w-4 inline mr-1" />
                    Time Period
                  </label>
                  <Select value={selectedDays} onValueChange={setSelectedDays}>
                    <SelectTrigger data-testid="days-select">
                      <SelectValue placeholder="Select Period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Last 7 Days</SelectItem>
                      <SelectItem value="30">Last 30 Days</SelectItem>
                      <SelectItem value="90">Last 90 Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">New Enquiries</p>
                      <p className="text-2xl font-bold" data-testid="new-enquiries-count">
                        {summary.total_new_enquiries}
                      </p>
                    </div>
                    <FileText className="h-10 w-10 text-blue-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Conversion Rate</p>
                      <p className="text-2xl font-bold" data-testid="conversion-rate">
                        {summary.overall_conversion_rate}%
                      </p>
                      <p className="text-xs text-slate-400">
                        {summary.total_won_enquiries} won / {summary.total_closed_enquiries} closed
                      </p>
                    </div>
                    <TrendingUp className="h-10 w-10 text-green-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">New Tickets</p>
                      <p className="text-2xl font-bold" data-testid="new-tickets-count">
                        {summary.total_new_tickets}
                      </p>
                    </div>
                    <Ticket className="h-10 w-10 text-orange-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Resolution Rate</p>
                      <p className="text-2xl font-bold" data-testid="resolution-rate">
                        {summary.overall_resolution_rate}%
                      </p>
                      <p className="text-xs text-slate-400">
                        {summary.total_closed_tickets} closed / {summary.total_new_tickets} total
                      </p>
                    </div>
                    <TrendingDown className="h-10 w-10 text-purple-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Branch Name Banner */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
            <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Showing trends for: {branchName}
            </h2>
            <p className="text-sm text-slate-600">
              Period: Last {selectedDays} days
            </p>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Lead Volume Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-500" />
                    Lead Volume (New Enquiries)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80" data-testid="lead-volume-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={formatDate}
                          tick={{ fontSize: 12 }}
                          stroke="#64748b"
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          stroke="#64748b"
                          allowDecimals={false}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="new_enquiries" 
                          name="New Enquiries"
                          stroke="#3b82f6" 
                          strokeWidth={2}
                          dot={{ fill: '#3b82f6', r: 3 }}
                          activeDot={{ r: 6 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="won_enquiries" 
                          name="Won Enquiries"
                          stroke="#22c55e" 
                          strokeWidth={2}
                          dot={{ fill: '#22c55e', r: 3 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              {/* Enquiry Conversion Rate Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-500" />
                    Enquiry Conversion Rate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80" data-testid="conversion-rate-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={formatDate}
                          tick={{ fontSize: 12 }}
                          stroke="#64748b"
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          stroke="#64748b"
                          domain={[0, 100]}
                          tickFormatter={(value) => `${value}%`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="enquiry_conversion_rate" 
                          name="Conversion Rate"
                          stroke="#22c55e" 
                          strokeWidth={2}
                          dot={{ fill: '#22c55e', r: 3 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              {/* Ticket Volume Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Ticket className="h-5 w-5 text-orange-500" />
                    Ticket Volume
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80" data-testid="ticket-volume-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={formatDate}
                          tick={{ fontSize: 12 }}
                          stroke="#64748b"
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          stroke="#64748b"
                          allowDecimals={false}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="new_tickets" 
                          name="New Tickets"
                          stroke="#f97316" 
                          strokeWidth={2}
                          dot={{ fill: '#f97316', r: 3 }}
                          activeDot={{ r: 6 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="closed_tickets" 
                          name="Closed Tickets"
                          stroke="#a855f7" 
                          strokeWidth={2}
                          dot={{ fill: '#a855f7', r: 3 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              {/* Ticket Resolution Rate Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="h-5 w-5 text-purple-500" />
                    Ticket Resolution Rate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80" data-testid="resolution-rate-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={formatDate}
                          tick={{ fontSize: 12 }}
                          stroke="#64748b"
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          stroke="#64748b"
                          domain={[0, 100]}
                          tickFormatter={(value) => `${value}%`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="ticket_resolution_rate" 
                          name="Resolution Rate"
                          stroke="#a855f7" 
                          strokeWidth={2}
                          dot={{ fill: '#a855f7', r: 3 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};
